/**
 * Who may pay, who owns, and the record of both (owner spec, 2026-10-09, verbatim in docs/BILLING-ROLES.md):
 * "Managers can add billing if allowed by owner and the manager can pass ownership with confirmation and code to another user so
 * that they can setup the account with the owners cc and pass it off or remove the cc on ownership transfer. Stripe needs an update
 * of the status so its easy to look up otherwise refunds are a nightmare. We need logs of that change on the admin dashboard with
 * names and emails and CC changes update logs. managers who put in the cc should see the cost. Owners can set a billing admin for
 * each location if they want default to owner. There should always be one owner. Owner and manager can be the same role. If a
 * manager is elected, permissions can be shared with switches and changed later to block if roles or employees change and trust
 * isnt there."
 *
 * Pure rules, one place, for the server, the web and the phone:
 *   - the switches an owner shares with a manager or admin (Billing, People, Settings), their defaults, and `canDo`;
 *   - the billing admin per location (default: the owner), who qualifies, and who gets the billing emails;
 *   - an ownership transfer's states, its refusals and the words of its confirm screen;
 *   - the Stripe Customer fields that make a business easy to look up (name, email, metadata, description);
 *   - the billing and ownership log's rows, their plain words and a formula-safe CSV.
 * Never a card number anywhere: brand and last 4 only.
 */
import type { Id, IsoDateTime, Membership, Organization, Role } from "./model.ts";
import { orderedLocations } from "./billing.ts";
import { csvLine } from "./teamwork.ts";

/* ─────────────── the switches ─────────────── */

export type Switch = "billing" | "people" | "settings";
export const SWITCHES: readonly Switch[] = ["billing", "people", "settings"];
export type Switches = Record<Switch, boolean>;

/** The owner's words on the switch (the spec's own list). */
export const SWITCH_LABEL: Record<Switch, string> = {
  billing: "Billing (add or change the card, see costs)",
  people: "People (invite, remove, change roles below manager)",
  settings: "Settings (hours, locations)",
};
/** What turning a switch off stops, said under it. */
export const SWITCH_HELP: Record<Switch, string> = {
  billing: "Off: they can't open Plan and billing, add or change the card, or see what the plan costs.",
  people: "Off: they can't invite, remove or change anyone. Check-ins and the shift still work for them.",
  settings: "Off: they can't change the business name, hours, or add or change locations.",
};

/**
 * Defaults when the owner hasn't set a switch (owner spec: "Default off for billing").
 *   - manager: Billing off; People on (managers invite and remove today, so nothing is taken from them on the day this ships);
 *     Settings off (managers couldn't change hours or locations before).
 *   - admin: everything on (admins had all three before; ASSUMED the owner wants that kept until they switch one off).
 *   - owner: everything, always. staff: nothing (their role never reaches these screens).
 */
export function defaultSwitches(role: Role): Switches {
  if (role === "setup") return { billing: false, people: false, settings: true };
  if (role === "owner" || role === "admin") return { billing: true, people: true, settings: true };
  if (role === "manager") return { billing: false, people: true, settings: false };
  return { billing: false, people: false, settings: false };
}

/** The switches as they are for this person now: the owner's choices over the defaults. Owners always have all. */
export function switchesOf(m: Pick<Membership, "role"> & { permissions?: Partial<Switches> }): Switches {
  const d = defaultSwitches(m.role);
  if (m.role === "owner" || m.role === "staff" || m.role === "setup") return d;
  const p = m.permissions ?? {};
  return { billing: typeof p.billing === "boolean" ? p.billing : d.billing, people: typeof p.people === "boolean" ? p.people : d.people, settings: typeof p.settings === "boolean" ? p.settings : d.settings };
}

/**
 * May this person do what the switch covers? "Owner and manager can be the same role": an owner can do everything a manager can,
 * so an owner never needs a second person or a second membership to run the shift and pay the bill.
 */
export function canDo(m: Pick<Membership, "role"> & { permissions?: Partial<Switches> }, sw: Switch): boolean {
  if (m.role === "owner") return true;
  // Setup (core setuprole.ts): the place's settings while setting it up, never people or billing (billing is the route's own prep rule).
  if (m.role === "setup") return sw === "settings";
  if (m.role === "staff") return false;
  return switchesOf(m)[sw];
}

/** Can this person's switches be set (managers and admins; an owner has everything, staff nothing to share). */
export const hasSwitches = (role: Role): boolean => role === "manager" || role === "admin";

/** The refusal when a switch is off: plain words and the one next step (ask the owner). */
export function switchOffMessage(sw: Switch, ownerName: string | null): string {
  const who = ownerName ?? "the owner";
  const what = sw === "billing" ? "Billing" : sw === "people" ? "People" : "Settings";
  return `${what} is turned off for you on this business. Ask ${who} to turn it on in People → Permissions.`;
}

/** A switch patch from a request body: only the three known names, each true or false. Null when nothing valid was sent. */
export function switchPatch(body: unknown): Partial<Switches> | null {
  if (!body || typeof body !== "object") return null;
  const out: Partial<Switches> = {};
  for (const s of SWITCHES) { const v = (body as Record<string, unknown>)[s]; if (typeof v === "boolean") out[s] = v; }
  return Object.keys(out).length ? out : null;
}

/* ─────────────── billing admins ─────────────── */

/** May this member be a location's billing admin: an owner, or a manager/admin whose Billing switch is on. */
export function canBeBillingAdmin(m: Pick<Membership, "role"> & { permissions?: Partial<Switches> }): boolean {
  return canDo(m, "billing");
}

type Member = Pick<Membership, "userId" | "role" | "email" | "displayName" | "createdAt"> & { permissions?: Partial<Switches> };

/** The first owner (oldest membership): the default billing admin and the Stripe Customer's owner. */
export function firstOwner<M extends Member>(members: readonly M[]): M | null {
  return [...members].filter((m) => m.role === "owner").sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.userId.localeCompare(b.userId))[0] ?? null;
}

/**
 * A location's billing admin now: the person the owner chose, while they still qualify (a member, owner or Billing on); otherwise
 * the owner ("default to owner"). A switch turned off or a person removed falls back to the owner on the next read.
 */
export function billingAdminFor<M extends Member>(org: Pick<Organization, "billingAdmins">, locationId: Id, members: readonly M[]): M | null {
  const chosen = org.billingAdmins?.[locationId];
  const m = chosen ? members.find((x) => x.userId === chosen) : undefined;
  if (m && canBeBillingAdmin(m)) return m;
  return firstOwner(members);
}

/** The business's billing admin for Stripe's one Customer email: the first location's (oldest first), else the owner. */
export function primaryBillingAdmin<M extends Member>(org: Pick<Organization, "billingAdmins">, locations: readonly { id: Id; orgId: Id; createdAt: IsoDateTime }[], members: readonly M[]): M | null {
  const first = orderedLocations(locations as never)[0] as { id: Id } | undefined;
  return first ? billingAdminFor(org, first.id, members) : firstOwner(members);
}

/** Everyone who gets the business's billing emails and notices: each location's billing admin, once each (the owner when none). */
export function billingContacts<M extends Member>(org: Pick<Organization, "billingAdmins">, locations: readonly { id: Id }[], members: readonly M[]): M[] {
  const out = new Map<Id, M>();
  for (const l of locations) { const m = billingAdminFor(org, l.id, members); if (m) out.set(m.userId, m); }
  if (!out.size) { const o = firstOwner(members); if (o) out.set(o.userId, o); }
  return [...out.values()];
}

/**
 * Who sees the plan's costs (owner spec: "managers who put in the cc should see the cost"): Billing on, or the person who added the
 * card on file, or a location's billing admin.
 */
export function seesCost(m: Member, org: Pick<Organization, "billingAdmins" | "billing">, locations: readonly { id: Id }[], members: readonly Member[]): boolean {
  if (canDo(m, "billing")) return true;
  if (org.billing?.cardAddedBy?.userId === m.userId && org.billing.cardOnFile) return true;
  return locations.some((l) => billingAdminFor(org, l.id, members)?.userId === m.userId);
}

/* ─────────────── ownership transfer ─────────────── */

export const TRANSFER_DAYS = 7;
export const TRANSFER_CODE_MINUTES = 15;
export const TRANSFER_CODE_TRIES = 5;

export type TransferStatus = "awaiting_code" | "awaiting_accept" | "accepted" | "declined" | "canceled" | "expired";
export type TransferCard = "keep" | "remove";
/** What the person who handed it over becomes: "leave", "manager" (a paying preparer who keeps the card), or "setup" (kept 14 days to help finish setup). */
export type OldOwnerChoice = "manager" | "leave" | "setup";

export interface Person { userId: Id; name: string; email: string }

/** One transfer (organizations.ownership_transfer, migration 0115). The code is kept only as a hash; never shown again. */
export interface OwnershipTransfer {
  id: Id;
  from: Person;
  to: Person;
  card: TransferCard;
  oldOwner: OldOwnerChoice;
  status: TransferStatus;
  createdAt: IsoDateTime;
  expiresAt: IsoDateTime;
  codeHash?: string;
  codeSentAt?: IsoDateTime;
  codeTries: number;
  confirmedAt?: IsoDateTime;
  endedAt?: IsoDateTime;
}

/** The status as it is now: a pending one past its 7 days is expired. */
export function transferStatus(t: OwnershipTransfer | undefined | null, now: Date): TransferStatus | null {
  if (!t) return null;
  if ((t.status === "awaiting_code" || t.status === "awaiting_accept") && now.getTime() >= Date.parse(t.expiresAt)) return "expired";
  return t.status;
}
export const isPendingTransfer = (s: TransferStatus | null): boolean => s === "awaiting_code" || s === "awaiting_accept";

/** Why this transfer can't start, or null. Never leaves a business without an owner: the old owner leaves only once the new one accepts. */
export function transferRefusal(o: { actor: Pick<Membership, "role" | "userId">; target: Pick<Membership, "role" | "userId" | "email"> | null; pending: boolean; sample?: boolean }): string | null {
  if (o.sample) return "This is the sample café: it has no owner to pass on.";
  if (o.actor.role !== "owner") return "Only an owner can pass on ownership.";
  if (!o.target) return "Pick someone on this business's team who has signed in.";
  if (o.target.userId === o.actor.userId) return "You already own this business. Pick someone else.";
  if (o.target.role === "owner") return "They're already an owner here.";
  if (!o.target.email) return "They need to sign in with an email first.";
  if (o.pending) return "A transfer is already waiting. Cancel it first to start another.";
  return null;
}

/** The confirm screen, in plain words, before the code is sent. */
export function transferConfirmWords(o: { business: string; to: Person; card: TransferCard; oldOwner: OldOwnerChoice; card4?: string | null }): { title: string; lines: string[]; button: string } {
  const first = o.to.name.split(/\s+/)[0] || o.to.name;
  return {
    title: `Make ${first} the owner of ${o.business}?`,
    lines: [
      `${o.to.name} (${o.to.email}) becomes the owner once they accept. They get an email and a note in the app.`,
      o.card === "keep" ? `The card on file${o.card4 ? ` (${o.card4})` : ""} stays. ${first} pays with it until they change it.` : `The card on file${o.card4 ? ` (${o.card4})` : ""} is removed from Stripe when ${first} accepts. ${first} adds their own card; health check-ins and sick reports keep working either way.`,
      o.oldOwner === "setup" ? "You stay for 14 days to help finish setting up, then your access ends." : o.oldOwner === "manager" ? "You stay on as a manager. They can change that later." : "You leave the business when they accept. Your records stay.",
      `We email you a 6-digit code to confirm. The request ends in ${TRANSFER_DAYS} days if they don't accept, and you can cancel it any time before.`,
    ],
    button: "Email me the code",
  };
}

/** The new owner's prompt (email and in-app). */
export function transferOfferWords(o: { business: string; from: Person; card: TransferCard }): { subject: string; text: string } {
  return {
    subject: `${o.from.name} wants to make you the owner of ${o.business}`,
    text: `${o.from.name} (${o.from.email}) asked to make you the owner of ${o.business} on CleanCleaver.\n\n`
      + (o.card === "keep" ? "Their card stays on file, so the plan keeps going. You can change the card in Plan and billing.\n\n" : "Their card is removed when you accept. Add your own card in Plan and billing to keep the plan going; health check-ins and sick reports always work.\n\n")
      + `Open CleanCleaver and tap Accept on the note at the top, or Not now. The request ends in ${TRANSFER_DAYS} days.`,
  };
}

/* ─────────────── the Stripe Customer, kept in sync for lookups ─────────────── */

/**
 * The Customer fields for a business (owner: "Stripe needs an update of the status so its easy to look up otherwise refunds are a
 * nightmare"). Stripe's limits (docs.stripe.com/api/metadata, read 2026-10-09): 50 keys, keys up to 40 characters, values up to 500;
 * name up to 256 characters, email up to 512 (docs.stripe.com/api/customers/update). Values are cut to fit. No card data (Stripe:
 * "Don't store any sensitive information (bank account numbers, card details, and so on) as metadata").
 */
export function stripeCustomerFields(o: { org: Pick<Organization, "id" | "name" | "testAccount">; owner: Person | null; billingAdmin: Person | null }): { name: string; email?: string; description: string; metadata: Record<string, string> } {
  const cut = (s: string, n: number) => (s.length > n ? s.slice(0, n) : s);
  const email = o.billingAdmin?.email ?? o.owner?.email;
  return {
    name: cut(o.org.name || "CleanCleaver business", 256),
    ...(email ? { email: cut(email, 512) } : {}),
    description: cut(`CleanCleaver · ${o.org.name} · owner ${o.owner?.name ?? "unknown"}`, 350),
    metadata: {
      orgId: o.org.id,
      ownerEmail: cut(o.owner?.email ?? "", 500),
      ownerName: cut(o.owner?.name ?? "", 500),
      billingAdminEmail: cut(email ?? "", 500),
      testAccount: o.org.testAccount === true ? "true" : "false",
    },
  };
}

/* ─────────────── the billing and ownership log ─────────────── */

export type BillingLogKind =
  | "card_added" | "card_changed" | "card_removed"
  | "ownership_requested" | "ownership_confirmed" | "ownership_accepted" | "ownership_declined" | "ownership_canceled" | "ownership_expired"
  | "billing_admin_changed" | "switch_changed" | "stripe_customer_synced"
  | "refund_requested" | "refund_approved" | "refund_denied" | "refund_created" | "refund_updated" | "refund_dashboard" | "dispute"
  | "credit_given";

export const BILLING_LOG_KINDS: readonly { key: BillingLogKind; label: string }[] = [
  { key: "card_added", label: "Card added" }, { key: "card_changed", label: "Card changed" }, { key: "card_removed", label: "Card removed" },
  { key: "ownership_requested", label: "Ownership requested" }, { key: "ownership_confirmed", label: "Ownership confirmed by code" },
  { key: "ownership_accepted", label: "Ownership accepted" }, { key: "ownership_declined", label: "Ownership declined" },
  { key: "ownership_canceled", label: "Ownership canceled" }, { key: "ownership_expired", label: "Ownership expired" },
  { key: "billing_admin_changed", label: "Billing admin changed" }, { key: "switch_changed", label: "Permission changed" },
  { key: "stripe_customer_synced", label: "Stripe customer updated" },
  { key: "refund_requested", label: "Refund requested" }, { key: "refund_approved", label: "Refund approved" }, { key: "refund_denied", label: "Refund denied" },
  { key: "refund_created", label: "Refund sent" }, { key: "refund_updated", label: "Refund status" }, { key: "refund_dashboard", label: "Refund made in Stripe" },
  { key: "dispute", label: "Dispute" }, { key: "credit_given", label: "Credit (not a refund)" },
];
export const isBillingLogKind = (v: unknown): v is BillingLogKind => BILLING_LOG_KINDS.some((k) => k.key === v);

export interface CardFacts { brand: string; last4: string }
export interface BillingLogEntry {
  id: Id;
  orgId: Id;
  orgName: string;
  at: IsoDateTime;
  kind: BillingLogKind;
  /** Who did it (null: Stripe or our own job). */
  actor: Person | null;
  /** Who it was about (the new owner, the person whose switch changed, the new billing admin). */
  subject?: Person | null;
  card?: CardFacts | null;
  fromCard?: CardFacts | null;
  /** Small facts only: switch name and value, location name, transfer id, Stripe ids, cents. Never a card number. */
  detail: Record<string, string | number | boolean | null>;
  /** The row in plain words, made once by billingLogText. */
  text: string;
}

const brandWord = (b: string) => (b ? b.charAt(0).toUpperCase() + b.slice(1).toLowerCase() : "Card");
/** "Visa ••4242". Last 4 only; anything that isn't four digits is dropped. */
export const cardLast4Words = (c: CardFacts | null | undefined): string => (c && /^\d{4}$/.test(c.last4) ? `${brandWord(c.brand)} ••${c.last4}` : "a card");
const who = (p: Person | null | undefined) => (p ? `${p.name} ${p.email}`.trim() : "Stripe");
const money = (cents: unknown) => (typeof cents === "number" ? `$${(cents / 100).toFixed(2)}` : "");

/** One row's words: when is the column; this is who and what. */
export function billingLogText(e: Omit<BillingLogEntry, "text" | "id">): string {
  const d = e.detail;
  const by = who(e.actor);
  switch (e.kind) {
    case "card_added": return `Card added ${cardLast4Words(e.card)} by ${by}`;
    case "card_changed": return `Card changed ${cardLast4Words(e.fromCard)} → ${cardLast4Words(e.card)}${e.actor ? ` (card page opened by ${by})` : ""}`;
    case "card_removed": return `Card removed ${cardLast4Words(e.card)}${e.actor ? ` by ${by}` : " in Stripe"}${d.why ? ` (${d.why})` : ""}`;
    case "ownership_requested": return `Ownership ${e.actor?.name ?? "?"} → ${e.subject?.name ?? "?"} requested by ${by} (to ${who(e.subject)}; card: ${d.card === "remove" ? "remove" : "keep"})`;
    case "ownership_confirmed": return `Ownership ${e.actor?.name ?? "?"} → ${e.subject?.name ?? "?"} confirmed with the emailed code by ${by}`;
    case "ownership_accepted": return `Ownership ${String(d.fromName ?? "?")} → ${e.actor?.name ?? "?"} accepted by ${by}${d.card === "remove" ? "; card removed" : "; card kept"}${d.oldOwner === "leave" ? `; ${String(d.fromName ?? "the old owner")} left` : `; ${String(d.fromName ?? "the old owner")} is now a manager`}`;
    case "ownership_declined": return `Ownership ${String(d.fromName ?? "?")} → ${e.actor?.name ?? "?"} declined by ${by}`;
    case "ownership_canceled": return `Ownership ${e.actor?.name ?? "?"} → ${e.subject?.name ?? "?"} canceled by ${by}`;
    case "ownership_expired": return `Ownership ${String(d.fromName ?? "?")} → ${e.subject?.name ?? "?"} expired after ${TRANSFER_DAYS} days`;
    case "billing_admin_changed": return `Billing admin for ${String(d.location ?? "a location")} changed to ${who(e.subject)} by ${by}`;
    case "switch_changed": return `${SWITCH_LABEL[d.switch as Switch]?.split(" (")[0] ?? String(d.switch)} turned ${d.on ? "on" : "off"} for ${who(e.subject)} by ${by}`;
    case "stripe_customer_synced": return `Stripe customer updated (${String(d.why ?? "sync")}): ${String(d.name ?? "")}, ${String(d.email ?? "")}`;
    case "refund_requested": return `Refund of ${money(d.cents)} requested by ${by}${d.reason ? ` (${String(d.reason)})` : ""}${d.needsApproval ? `; needs ${String(d.approverWord ?? "a sign-off from above")}` : ""}`;
    case "refund_approved": return `Refund of ${money(d.cents)} approved by ${by}${d.requestedBy ? ` (requested by ${String(d.requestedBy)})` : ""}`;
    case "refund_denied": return `Refund of ${money(d.cents)} denied by ${by}`;
    case "refund_created": return `Refund of ${money(d.cents)} sent to Stripe (${String(d.refundId ?? "")}, ${String(d.status ?? "pending")})${d.requestedBy ? `; requested by ${String(d.requestedBy)}` : ""}${d.approvedBy ? `; approved by ${String(d.approvedBy)}` : ""}`;
    case "refund_updated": return `Refund ${String(d.refundId ?? "")} is ${String(d.status ?? "")}${d.failureReason ? ` (${String(d.failureReason)})` : ""}`;
    case "refund_dashboard": return `Refund of ${money(d.cents)} made in the Stripe dashboard (${String(d.refundId ?? "")}, ${String(d.status ?? "")})`;
    case "dispute": return `Dispute ${String(d.disputeId ?? "")}: ${String(d.status ?? "")} for ${money(d.cents)}${d.reason ? ` (${String(d.reason)})` : ""}`;
    case "credit_given": return `Credit of ${money(d.cents)} given by ${by} (a balance credit, not a refund)`;
  }
}

/** The log as a spreadsheet: when, business, what, who, their email, about whom, card, the words. Formulas neutralized (csvLine). */
export function billingLogCsv(rows: readonly BillingLogEntry[]): string {
  const label = (k: BillingLogKind) => BILLING_LOG_KINDS.find((x) => x.key === k)?.label ?? k;
  return [
    csvLine(["When", "Business", "Business id", "What", "Who", "Who email", "About", "About email", "Card", "Card before", "Details"]),
    ...rows.map((r) => csvLine([r.at, r.orgName, r.orgId, label(r.kind), r.actor?.name ?? "Stripe", r.actor?.email ?? "", r.subject?.name ?? "", r.subject?.email ?? "", r.card ? cardLast4Words(r.card) : "", r.fromCard ? cardLast4Words(r.fromCard) : "", r.text])),
  ].join("\n") + "\n";
}

export interface BillingLogFilter { orgId?: Id; kind?: BillingLogKind; q?: string; from?: IsoDateTime; to?: IsoDateTime; limit?: number }
/** The filters the log pages offer (business, kind, a name/email/last-4 search, dates). Newest first. */
export function filterBillingLog(rows: readonly BillingLogEntry[], f: BillingLogFilter): BillingLogEntry[] {
  const q = f.q?.trim().toLowerCase();
  return rows
    .filter((r) => (!f.orgId || r.orgId === f.orgId) && (!f.kind || r.kind === f.kind) && (!f.from || r.at >= f.from) && (!f.to || r.at <= f.to)
      && (!q || [r.orgName, r.text, r.actor?.email, r.subject?.email, r.card?.last4].some((x) => x?.toLowerCase().includes(q))))
    .sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id))
    .slice(0, Math.min(Math.max(1, f.limit ?? 200), 2000));
}
